import { useCustomizationContext } from '@apps/card/routes/CardCustomizer';
import type { CardEnvelopeReturnAddressLayout, CardEnvelopeRecipientAddressLayout } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import type { CardDieCut } from '@apps/card/routes/CardCustomizer/components/CardDieCut';
import { CardPaperType } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { StationeryProduct } from '@shared/utils/print-price/types';
import type { AddAttributeFunction } from '../state/state.mapping';
import { getFoilConfigs } from './useDeriveFoilIds';
import { findFoilLayer } from '@apps/card/utils';
import { isFoilTemplate } from '@apps/card/utils/isFoilTemplate';
import { readTaylorAttributes } from './taylor.readers';
import type { TaylorAttributeId } from './taylor.readers.types';

const optionIdToCardPaperType = (optionId: string): CardPaperType => {
  switch (optionId) {
    case 'signature': {
      return CardPaperType.signature;
    }
    default: {
      return optionId as CardPaperType;
    }
  }
};

export const useTaylorMappings = () => {
  const [_customizations, updateCustomizations] = useCustomizationContext(draft => draft.config.customizations);
  const [root, updateDraftRoot] = useCustomizationContext(draft => draft);

  return useEventCallback((addAttribute: AddAttributeFunction, product: StationeryProduct) => {
    // Magnets mail in Taylor's A6 envelopes, which come in exactly three print
    // modes: blank, return address on the back flap, and return address on the
    // back flap plus a variable recipient front (Kaila, #joy-taylor 2026-10-07).
    // Recipient addressing is never offered without a return address, so the
    // two choices are coupled here: turning recipients on puts the return
    // address on the flap, and dropping the return address turns recipients
    // off. The product's attribute options already exclude a front return
    // address for magnets; this keeps the remaining pair consistent.
    const recipientsRequireReturnAddress = product.productType === 'magnet';

    const isFoil = isFoilTemplate(root.template);
    // Maps target OptionId to real ColorId on set
    const foilConfigs = getFoilConfigs(product, 'foil');

    const setters: Record<TaylorAttributeId, (newState: string) => void> = {
      paperType: newState => {
        updateCustomizations(draftData => {
          draftData.paperType = optionIdToCardPaperType(newState);
        });
      },
      shape: newState => {
        updateCustomizations(draftData => {
          draftData.shape = newState as CardDieCut;
        });
      },
      returnAddress: newState => {
        updateDraftRoot(draft => {
          draft.config.card.envelope.layout = newState as CardEnvelopeReturnAddressLayout;
          if (recipientsRequireReturnAddress && newState === 'none' && draft.config.card.envelope.recipientAddressLayout === 'front') {
            draft.config.card.envelope.recipientAddressLayout = 'none' as CardEnvelopeRecipientAddressLayout;
          }
        });
      },
      recipientAddress: newState => {
        updateDraftRoot(draft => {
          draft.config.card.envelope.recipientAddressLayout = newState as CardEnvelopeRecipientAddressLayout;
          if (recipientsRequireReturnAddress && newState === 'front' && draft.config.card.envelope.layout === 'none') {
            draft.config.card.envelope.layout = 'back' as CardEnvelopeReturnAddressLayout;
          }
        });
      },
      foil: newState => {
        if (!isFoil) {
          return;
        }
        // Get colorId for matching option
        const foilColorId = foilConfigs?.find(f => f.optionId === newState)?.colorId;
        updateDraftRoot(draft => {
          const layer = findFoilLayer(draft.config.card.front.layers);
          if (layer?.foilData) {
            layer.foilData.colorId = foilColorId ?? 'GOLD';
          }
        });
      },
      cardBack: () => {},
    };

    readTaylorAttributes(product, root.template.themeId).forEach(reader => {
      addAttribute({ ...reader, setOptionId: setters[reader.productAttributeId] }, product);
    });
  });
};
